import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiFetch } from '@/lib/apiFetch';
import DashboardHeader from '@/components/DashboardHeader';
import AppFooter from '@/components/AppFooter';
import { Activity, CheckCircle2, XCircle, RefreshCw, Database, Plug, Webhook, KeyRound } from 'lucide-react';

const CHECK_ICONS = {
  db: Database,
  ghl_oauth: Plug,
  openai: KeyRound,
  webhook_engine: Webhook,
};

const STATUS_COLORS = {
  success: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  warning: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  error: 'text-red-400 bg-red-500/10 border-red-500/20',
};

function formatTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

export default function AdminHealthPage() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  // Track latest fetch so stale (earlier) responses can't overwrite fresh state.
  // This fixes the classic React race where useEffect re-fires on auth init and
  // a pre-cookie 401 response races behind a later 200 — leaving the user with
  // a misleading 401 error displayed even though DevTools shows the request as 200 OK.
  const fetchIdRef = useRef(0);

  const fetchHealth = async () => {
    const myId = ++fetchIdRef.current;
    setLoading(true);
    setErr('');
    try {
      // apiFetch handles auto-refresh-and-retry on 401 transparently
      const { data: json } = await apiFetch('/api/admin/health');
      if (myId !== fetchIdRef.current) return;
      setData(json);
      setErr('');
    } catch (e) {
      if (myId !== fetchIdRef.current) return;
      const status = e.response?.status;
      if (status === 401) {
        setErr('Your session has expired. Please log in again.');
      } else if (status === 403) {
        setErr('Admin access required.');
      } else {
        setErr(e.response?.data?.detail || e.message || 'Failed to load health data');
      }
    } finally {
      if (myId === fetchIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchHealth();
    }
  }, [user]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#141414] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#7B2FFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#141414]" data-testid="admin-not-authorized">
        <DashboardHeader />
        <main className="max-w-[700px] mx-auto px-6 py-16 text-center">
          <h2 className="text-2xl font-bold text-[#EDEDF0] mb-2" style={{ fontFamily: "'Unbounded', sans-serif" }}>
            Not authorised
          </h2>
          <p className="text-sm text-[#A1A1AA]">This page is restricted to admin accounts.</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#141414]" data-testid="admin-health-page">
      <DashboardHeader />
      <main className="max-w-[900px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#7B2FFF]">Internal · Ops</span>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#EDEDF0] mt-1 flex items-center gap-3" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              <Activity className="w-6 h-6 text-[#7B2FFF]" />
              System Health
            </h1>
            <p className="text-sm text-[#A1A1AA] mt-1" data-testid="last-checked-timestamp">
              Last checked: {data?.checked_at ? formatTime(data.checked_at) : '—'}
            </p>
          </div>
          <button
            data-testid="refresh-health-button"
            onClick={fetchHealth}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-xs text-[#A1A1AA] hover:text-[#EDEDF0] border border-[#27272A] rounded-md hover:border-[#7B2FFF]/30 transition-all disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {err && (
          <div data-testid="admin-health-error" className="mb-6 p-3 border border-red-500/30 rounded-md bg-red-500/10 text-red-400 text-sm">
            {err}
          </div>
        )}

        {data && (
          <>
            {/* 1. System Health Checks */}
            <section className="mb-8">
              <h2 className="text-sm font-semibold text-[#EDEDF0] mb-3" style={{ fontFamily: "'Unbounded', sans-serif" }}>
                System Health Checks
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="health-checks">
                {data.checks.map((c) => {
                  const Icon = CHECK_ICONS[c.id] || Activity;
                  return (
                    <div
                      key={c.id}
                      data-testid={`health-check-${c.id}`}
                      className="border border-[#27272A] rounded-md bg-[#1A1A1A] p-4 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-md bg-[#7B2FFF]/10 flex items-center justify-center">
                          <Icon className="w-4 h-4 text-[#7B2FFF]" />
                        </div>
                        <span className="text-sm text-[#EDEDF0]">{c.label}</span>
                      </div>
                      {c.ok ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-400" data-testid={`health-check-${c.id}-ok`} />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-400" data-testid={`health-check-${c.id}-fail`} />
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* 2. App Stats */}
            <section className="mb-8">
              <h2 className="text-sm font-semibold text-[#EDEDF0] mb-3" style={{ fontFamily: "'Unbounded', sans-serif" }}>
                App Stats
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="app-stats">
                <StatCard label="Total users" value={data.stats.total_users} testid="stat-total-users" />
                <StatCard label="Active installations" value={data.stats.active_installations} testid="stat-active-installations" />
                <StatCard label="Total webhook events" value={data.stats.total_webhook_events} testid="stat-total-webhook-events" />
              </div>
            </section>

            {/* 3. Recent Webhook Events */}
            <section>
              <h2 className="text-sm font-semibold text-[#EDEDF0] mb-3" style={{ fontFamily: "'Unbounded', sans-serif" }}>
                Recent Webhook Events
              </h2>
              <div className="border border-[#27272A] rounded-md bg-[#1A1A1A] overflow-hidden" data-testid="recent-webhook-events">
                {data.recent_webhook_events.length === 0 ? (
                  <p className="text-sm text-[#71717A] p-6 text-center" data-testid="no-events">
                    No events recorded yet
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-[#111111] text-[#71717A] text-xs uppercase tracking-wider">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-medium">Timestamp</th>
                        <th className="text-left px-4 py-2.5 font-medium">Event</th>
                        <th className="text-left px-4 py-2.5 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recent_webhook_events.map((evt, idx) => {
                        const status = (evt.status || 'success').toLowerCase();
                        const colorClass = STATUS_COLORS[status] || STATUS_COLORS.success;
                        return (
                          <tr key={idx} className="border-t border-[#27272A]" data-testid={`event-row-${idx}`}>
                            <td className="px-4 py-3 text-[#A1A1AA] font-mono text-xs">{formatTime(evt.timestamp)}</td>
                            <td className="px-4 py-3 text-[#EDEDF0] font-mono text-xs">{evt.event_type || '—'}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-block px-2 py-0.5 rounded text-xs border ${colorClass}`}>
                                {status}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </section>
          </>
        )}

        <AppFooter />
      </main>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="border border-[#27272A] rounded-md bg-[#1A1A1A] p-4 transition-all hover:border-[#7B2FFF]/30" data-testid={testid}>
      <p className="text-xs text-[#71717A] uppercase tracking-wider mb-2">{label}</p>
      <p className="text-2xl font-bold text-[#EDEDF0]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{value}</p>
    </div>
  );
}
